import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { useToast } from '@/contexts/ToastContext';
import { ICONS, Icon, InfoShell } from './InfoShell';

// New-design Contact page (/contact): three channel cards, the inquiry form
// beside the office addresses, and the two office maps. The form sends the
// same fields to the same endpoint as the classic page.

const FORM_URL = 'https://readdy.ai/api/form/d88su5aj08n3rflih9cg';
const MAPS = {
  bd: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d233668.38704001157!2d90.2791927058338!3d23.7807777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8b087026b81%3A0x8fa563bbdd5904c2!2sDhaka%2C%20Bangladesh!5e0!3m2!1sen!2sus!4v1716451200000!5m2!1sen!2sus',
  korea:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d101298.96984807027!2d126.82762146968459!3d37.38002317839487!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357b6a2a1b5b5b5b%3A0x0!2sAnyang-si%2C%20Gyeonggi-do%2C%20South%20Korea!5e0!3m2!1sen!2sus!4v1716451200000!5m2!1sen!2sus',
};

export default function ContactV2() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [messageError, setMessageError] = useState('');
  const [count, setCount] = useState(0);

  const email = t('contact.channels.email.value');
  const phone = t('contact.channels.whatsapp.value');
  const wa = phone.replace(/[^\d]/g, '');

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length < 20) {
      setMessageError(t('contact.form.messageMinError'));
      return;
    }
    setMessageError('');
    setStatus('submitting');
    const data = new URLSearchParams();
    form.querySelectorAll('input[name], textarea[name], select[name]').forEach((f) => {
      const el = f as HTMLInputElement;
      data.append(el.name, el.type === 'checkbox' ? (el.checked ? 'yes' : 'no') : el.value);
    });
    const done = () => {
      setStatus('success');
      showToast(t('contact.success.toast'));
    };
    fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() }).then(done, done);
  };

  const channels = [
    { icon: ICONS.mail, label: t('contact.channels.email.label'), value: email, note: t('contact.channels.email.note'), href: email.includes('@') ? `mailto:${email}` : '' },
    { icon: ICONS.chat, label: t('contact.channels.whatsapp.label'), value: phone, note: t('contact.channels.whatsapp.note'), href: wa ? `https://wa.me/${wa}` : '' },
    { icon: ICONS.building, label: t('contact.channels.hq.label'), value: t('contact.channels.hq.value'), note: t('contact.channels.hq.note'), href: '' },
  ];
  const offices = [
    { k: 'bd', title: t('contact.offices.bd.title'), company: t('contact.offices.bd.company'), address: t('contact.offices.bd.address') },
    { k: 'korea', title: t('contact.offices.korea.title'), company: t('contact.offices.korea.company'), address: t('contact.offices.korea.address') },
  ] as const;

  return (
    <>
      <Seo pageKey="contact" />
      <InfoShell label={t('contact.hero.label')} title={t('contact.hero.heading')} lede={t('contact.hero.subtitle')} help={false}>
        {/* Channels */}
        <section className="isec">
          <div className="wrap">
            <div className="cchan">
              {channels.map((c, i) => {
                const body = (
                  <>
                    <span className="ic">
                      <Icon d={c.icon} />
                    </span>
                    <small>{c.label}</small>
                    <b>{c.value}</b>
                    <span className="nt">{c.note}</span>
                    {c.href && <Icon d={ICONS.arrow} className="go" />}
                  </>
                );
                return c.href ? (
                  <a key={i} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                    {body}
                  </a>
                ) : (
                  <div key={i} className="rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                    {body}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Form + offices */}
        <section className="isec soft">
          <div className="wrap cform">
            <div className="cfcard rv">
              <span className="lbl">{t('infoV2.contact.write', 'Write to us')}</span>
              <h2>{t('contact.sendMessage.heading')}</h2>
              {status === 'success' ? (
                <div className="cdone">
                  <span className="ic">
                    <Icon d={ICONS.check} />
                  </span>
                  <h3>{t('contact.success.heading')}</h3>
                  <p>{t('contact.success.body')}</p>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setStatus('idle');
                      setCount(0);
                    }}
                  >
                    {t('contact.form.sendAnother')}
                  </button>
                </div>
              ) : (
                <form data-readdy-form action={FORM_URL} method="POST" onSubmit={onSubmit} className="ifm">
                  <label>
                    <span>{t('contact.form.name')} *</span>
                    <input type="text" name="name" required placeholder={t('contact.form.namePlaceholder')} />
                  </label>
                  <label>
                    <span>{t('contact.form.email')} *</span>
                    <input type="email" name="email" required placeholder={t('contact.form.emailPlaceholder')} />
                  </label>
                  <label>
                    <span>{t('contact.form.phone')}</span>
                    <input type="text" name="phone" placeholder={t('contact.form.phoneHelper')} />
                  </label>
                  <label>
                    <span>{t('contact.form.subject')} *</span>
                    <select name="subject" required defaultValue="">
                      <option value="">{t('contact.form.subjectPlaceholder')}</option>
                      <option value="Product Inquiry">{t('contact.form.subjectProductInquiry')}</option>
                      <option value="Pharmacy Partnership">{t('contact.form.subjectPharmacyPartnership')}</option>
                      <option value="Influencer Collaboration">{t('contact.form.subjectInfluencer')}</option>
                      <option value="Medi.K Seoul Medical Tourism Inquiry">{t('contact.form.subjectMediK')}</option>
                      <option value="Pick.K Seoul Influencer Program">{t('contact.form.subjectPickK')}</option>
                      <option value="Counterfeit Report">{t('contact.form.subjectCounterfeit')}</option>
                      <option value="Other">{t('contact.form.subjectOther')}</option>
                    </select>
                  </label>
                  <label className="full">
                    <span>{t('contact.form.message')} *</span>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder={t('contact.form.messagePlaceholder')}
                      onChange={(e) => setCount(e.target.value.length)}
                    />
                    <em className={messageError ? 'err' : undefined}>{messageError || `${count} / 500`}</em>
                  </label>
                  <label className="full chk">
                    <input type="checkbox" name="consent" required />
                    <i>
                      <Icon d={ICONS.check} />
                    </i>
                    <span>{t('contact.form.consent')}</span>
                  </label>
                  <button type="submit" className="btn dark full" disabled={status === 'submitting'}>
                    {status === 'submitting' ? t('contact.form.sending') : t('contact.form.submit')}
                    <Icon d={ICONS.arrow} />
                  </button>
                </form>
              )}
            </div>

            <aside className="coff rv" style={{ ['--d' as string]: '.1s' }}>
              <span className="lbl">{t('infoV2.contact.offices', 'Our offices')}</span>
              {offices.map((o) => (
                <div key={o.k} className="ofc">
                  <span className="ic">
                    <Icon d={ICONS.pin} />
                  </span>
                  <div>
                    <small>{o.title}</small>
                    <b>{o.company}</b>
                    <p>{o.address}</p>
                  </div>
                </div>
              ))}
              <div className="ofc">
                <span className="ic">
                  <Icon d={ICONS.clock} />
                </span>
                <div>
                  <small>{t('infoV2.contact.hours', 'Hours')}</small>
                  <p>{t('contact.channels.whatsapp.note')}</p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Maps */}
        <section className="isec">
          <div className="wrap cmaps">
            {offices.map((o, i) => (
              <figure key={o.k} className="rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                <div className="mp">
                  <iframe src={MAPS[o.k]} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={o.title} allowFullScreen />
                </div>
                <figcaption>
                  <b>{o.title}</b>
                  <span>{o.address}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      </InfoShell>
    </>
  );
}
